import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import {
    environmentDetails,
    isConfigDiskFulfilled,
    isMandatoryTagsFullfilled,
    orderId,
    quickConfigDetails,
    sspSubmitOrder,
    toggleSspModels,
    validationErrorCode,
} from 'pages/provisioning/store/aksns';
import { sspSubmitOrder as eiapSspSubmitOrder } from 'pages/provisioning/store/eip-stack';
import ProvisioningStyles from 'pages/provisioning/aksns/style';
import { eiapSave } from 'utils/constant';
import { navigateToWithPageReload } from 'utils';
import {
    formOrderObjFromCart,
    formOrderObjFromOrderDetails,
    formOrderObjFromEiapObject,
    resetEnvironmentDetails,
    resetOrder,
    resetToggleSspModal,
} from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/aksns/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/aksns/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/aksns/SspForm'));
const Tags = React.lazy(() => import('pages/provisioning/aksns/Tags'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aksns/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aksns/UpdatedToCartConfirmModal'));
const ErrorCodeModal = React.lazy(() => import('pages/provisioning/aksns/ErrorCodeModal'));
const QuickConfig = React.lazy(() => import('pages/provisioning/aksns/QuickConfig'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/aksns/ShowCostDetails'));

const AksProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetIsConfigDiskFulfilled] = useRecoilState(isConfigDiskFulfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetQuickConfig] = useRecoilState(quickConfigDetails);
    const [, setErrorCode] = useRecoilState(validationErrorCode);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const queryParams = new URLSearchParams(window.location.search);
    const componentId = queryParams.get('componentId');
    const [eiapOrder] = useRecoilState(eiapSspSubmitOrder);
    const axios = useAxios();
    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }
    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                emptyCost: costs,
            }));
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
            }));
        } else if (operation === eiapSave) {
            if (!eiapOrder?.appProfile || !eiapOrder?.landingZoneDetails || !eiapOrder?.sector || !eiapOrder?.environment || !eiapOrder?.region) {
                navigateToWithPageReload('/');
            }

            let orderObj;
            if (eiapOrder?.components?.[componentId]) {
                orderObj = await formOrderObjFromCart({ itemDetail: eiapOrder?.components?.[componentId] });
            } else {
                orderObj = await formOrderObjFromEiapObject(eiapOrder, data.cloudService);
            }
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data.cloudService,
                validationStatus: 'clearValidation',
                ...orderObj,
                eiapState: eiapOrder,
                operation: eiapSave,
                componentId,
                isTouched: false,
                emptyCost: costs,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                sku: data?.images?.sku,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }
    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetEnvDetails(resetEnvironmentDetails);
        resetIsMandatoryTagsFullfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetIsConfigDiskFulfilled(false);
        resetQuickConfig(undefined);
        setErrorCode('');
    };
    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Helmet>
                <title>Azure AKS Namespace Provisioning - DevX Infra</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <Tags />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <QuickConfig />
                        <ShowCostDetails />
                        <ErrorCodeModal />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default AksProvisioningPage;
